import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../../context/AppContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const CartDrawer = () => {
  const { isCartOpen, setIsCartOpen, cart, removeFromCart } = useApp();

  const subtotal = cart.reduce((acc, item) => {
    const price = parseInt(item.price.replace('$', ''));
    return acc + (price * item.quantity);
  }, 0);

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsCartOpen(false)}
            className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-[60]"
          />
          <motion.div 
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-parchment shadow-2xl z-[70] flex flex-col"
          >
            <div className="p-8 border-b border-cobalt/10 flex justify-between items-center bg-white/50">
              <div>
                <h2 className="text-2xl font-bold text-cobalt">Manifest</h2>
                <p className="text-[10px] font-bold uppercase tracking-widest text-amber">Current Deployment: {cart.length} items</p>
              </div>
              <button 
                onClick={() => setIsCartOpen(false)}
                className="p-2 hover:bg-cobalt/5 rounded-full transition-colors"
                aria-label="Close Manifest"
              >
                <SafeIcon icon={FiIcons.FiX} className="text-2xl" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-6">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center opacity-40">
                  <SafeIcon icon={FiIcons.FiPackage} className="text-6xl mb-4" />
                  <p className="font-serif italic text-lg">Your crate is currently empty.</p>
                  <button 
                    onClick={() => setIsCartOpen(false)}
                    className="mt-6 text-xs font-bold uppercase tracking-widest border-b border-cobalt pb-1"
                  >
                    Continue Scouting
                  </button>
                </div>
              ) : (
                cart.map((item) => (
                  <motion.div 
                    layout
                    key={item.id} 
                    className="flex gap-4 group journal-border p-4 bg-white/40"
                  >
                    <div className="w-20 h-20 bg-parchment-dark overflow-hidden shrink-0">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-sm text-cobalt leading-tight">{item.name}</h4>
                      <p className="text-xs text-ink-light/60 mt-1">{item.price} x {item.quantity}</p>
                      <button 
                        onClick={() => removeFromCart(item.id)}
                        className="text-[10px] uppercase font-bold text-amber mt-4 hover:text-ink transition-colors"
                      >
                        Remove from Deployment
                      </button>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-8 border-t border-cobalt/10 bg-white/80 backdrop-blur-md">
                <div className="flex justify-between items-end mb-8">
                  <span className="text-xs font-bold uppercase tracking-widest text-ink-light">Logistics Total</span>
                  <span className="text-3xl font-bold text-cobalt">${subtotal}</span>
                </div>
                <button className="w-full bg-cobalt text-white py-5 font-black uppercase tracking-[0.2em] text-[11px] hover:bg-amber transition-all shadow-lg shadow-cobalt/20">
                  Finalize Shipment
                </button>
                <p className="text-[10px] text-center text-ink-light/40 mt-4 font-serif italic">
                  Duties and taxes calculated at dispatch.
                </p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;